import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import Countdown from "react-countdown";
import { sha384 } from "crypto-hash";
import { v4 as uuid } from "uuid";
import config from "config/config";

import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";
import { resetPassword } from "service/userservice/resetPassword";
import { updateUsername } from "service/userservice/updateUsername";
import { getUserProfile } from "service/userservice/getUserProfile";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";

export default function UserSecurityDialog({ dialogState, setDialogState }) {
  const [step, setStep] = useState(1);
  const UUID = uuid();
  const [otpError, setotpError] = useState("");
  const [showOtpError, setshowOtpError] = useState(false);
  const [usernameError, setusernameError] = useState("");
  const [showUsernameError, setshowUsernameError] = useState(false);
  const [passwordError, setpasswordError] = useState("");
  const [showPasswordError, setshowPasswordError] = useState(false);
  const [openEyeCurrentPassword, setOpenEyeCurrentPassword] = useState(true);
  const [openEyePassword, setOpenEyePassword] = useState(true);
  const [openEyeConfirmPassword, setOpenEyeConfirmPassword] = useState(true);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [errorText, setErrorText] = useState("");
  const [showErrorText, setShowErrorText] = useState(false);
  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const onOptionChange = (e) => setUpdateItem(e.target.value);
  const [updateItem, setUpdateItem] = useState("password");
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);

  useEffect(() => {
    setStep(1);
    setUpdateItem("password");
  }, [dialogState]);

  async function submitStepOne(data) {
    let result = true;
    setLoading(true);
    let loggedInUserProfile = await getUserProfile(
      "",
      "",
      "",
      "",
      "",
      user.userId
    ).catch((error) => {
      console.log(error);
    });
    setLoading(false);

    let hashedCurrentPassword = (await sha384(data.currentPassword))
      .toString()
      .substring(15, 30);
    if (loggedInUserProfile.password === hashedCurrentPassword) {
      if (updateItem === "username" || updateItem === "usernameandpwd") {
        setLoading(true);
        let userProfile = await getUserProfile(data.username).catch((error) => {
          console.log(error);
        });
        if (userProfile) {
          setLoading(false);
          setshowUsernameError(true);
          setusernameError(
            "Username is not available, please try a different username."
          );
          result = false;
        }
      }

      if (updateItem === "password" || updateItem === "usernameandpwd") {
        let hashedPassword = (await sha384(data.newPassword))
          .toString()
          .substring(15, 30);

        if (
          loggedInUserProfile.userCredentialHistory.some(
            (x) => x === hashedPassword
          )
        ) {
          setLoading(false);
          setpasswordError(
            "New Password cannot be one of the previous 15 passwords."
          );
          setshowPasswordError(true);
          result = false;
        }
      }

      if (result) {
        let passcode = randomNumberInRange(100000, 999999);
        setConfirmationPassCode(passcode.toString());
        let securitycode = randomNumberInRange(100000, 999999);
        let sec =
          parseInt(securitycode.toString().substring(3, 4)) === 0
            ? 1
            : parseInt(securitycode.toString().substring(3, 4));
        let confirmationcode = passcode * sec;
        setLoading(true);
        let contentDictionary = {};
        contentDictionary.confirmationcode = confirmationcode.toString();
        contentDictionary.username = user.firstName;
        contentDictionary.securitycode = securitycode.toString();

        await sendEmailNotification(user.emailId, 12, contentDictionary)
          .then((response) => {
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
            setStep(2);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else {
      setShowErrorText(true);
      setErrorText("Current Password doesn't match with our records.");
    }
  }

  async function submitStepTwo(formData) {
    if (passcodeExpired) {
      setotpError("The code has now expired. Please click Resend Code");
      setshowOtpError(true);
    } else {
      if (
        formData.verificationCode === confirmationPassCode ||
        (config.ENABLE_DEFAULT_OTP === "true" &&
          formData.verificationCode === config.DEFAULT_OTP)
      ) {
        if (!csrUser?.userId) {
          if (updateItem === "username" || updateItem === "usernameandpwd") {
            setLoading(true);
            await updateUsername(user.userId, formData.username)
              .then((data) => {
                setLoading(false);
                setStep(3);
              })
              .catch((error) => {
                setLoading(false);
                console.log(error);
              });
          }

          if (updateItem === "password" || updateItem === "usernameandpwd") {
            let hashedPassword = (await sha384(formData.newPassword))
              .toString()
              .substring(15, 30);
            setLoading(true);
            await resetPassword(user.userId, hashedPassword)
              .then((data) => {
                setLoading(false);
                setStep(3);
              })
              .catch((error) => {
                setLoading(false);
                console.log(error);
              });
          }
        } else {
          alert("You do not have permission to update security settings.");
        }
      } else {
        setotpError(
          "The code verification you entered has failed. Please check again and enter the code received in your email."
        );
        setshowOtpError(true);
      }
    }
  }

  const toggleCurrentPasswordEye = () => {
    setOpenEyeCurrentPassword(!openEyeCurrentPassword);
  };
  const togglePasswordEye = () => {
    setOpenEyePassword(!openEyePassword);
  };

  const toggleConfirmPasswordEye = () => {
    setOpenEyeConfirmPassword(!openEyeConfirmPassword);
  };

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <Link
          to=""
          className="mb-4 mr-5"
          onClick={async () => {
            let passcode = randomNumberInRange(100000, 999999);
            setConfirmationPassCode(passcode.toString());
            let securitycode = randomNumberInRange(100000, 999999);
            let sec =
              parseInt(securitycode.toString().substring(3, 4)) === 0
                ? 1
                : parseInt(securitycode.toString().substring(3, 4));
            let confirmationcode = passcode * sec;

            setLoading(true);

            let contentDictionary = {};
            contentDictionary.confirmationcode = confirmationcode.toString();
            contentDictionary.username = user.firstName;
            contentDictionary.securitycode = securitycode.toString();

            await sendEmailNotification(
              user.emailId,
              12,
              contentDictionary
            ).catch((error) => {
              console.log(error);
            });
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
          }}
        >
          Resend Code
        </Link>
      );
    } else {
      return (
        <p className="text-sm">
          Code expires in {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  return (
    <Dialog
      open={dialogState}
      onClose={() => setDialogState(false)}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center   overflow-y-auto">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            Security Settings
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label="close manage security settings window"
            onClick={() => setDialogState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            Manage your username and password settings
          </Dialog.Description>
          <hr />
          <EverlakeLoadingOverlay active={loading}>
            {step === 1 && (
              <form onSubmit={handleSubmit(submitStepOne)}>
                <div className="grid grid-cols-1 gap-4">
                  <div className="w-full mx-auto sm:w-4/5">
                    <label className="inline-block mr-6 items-center mb-2">
                      Please select the settings to update
                    </label>
                    <br />
                    <div className="inline-block mr-6 mb-3">
                      <label className="inline-flex items-center font-normal">
                        <input
                          type="radio"
                          value="password"
                          autoComplete="off"
                          checked={updateItem === "password"}
                          onClick={onOptionChange}
                          className="mr-2"
                          {...register("updateType")}
                        />
                        Password
                      </label>
                    </div>
                    <div className="inline-block mr-6">
                      <label className="inline-flex items-center  font-normal">
                        <input
                          type="radio"
                          value="username"
                          checked={updateItem === "username"}
                          onClick={onOptionChange}
                          className="mr-2"
                          {...register("updateType")}
                        />
                        Username
                      </label>
                    </div>
                    <div className="inline-block mr-6">
                      <label className="inline-flex items-center font-normal">
                        <input
                          type="radio"
                          value="usernameandpwd"
                          checked={updateItem === "usernameandpwd"}
                          onClick={onOptionChange}
                          className="mr-2"
                          {...register("updateType")}
                        />
                        Username & Password
                      </label>
                    </div>
                    <div></div>
                    {/* Username */}
                    {(updateItem === "username" ||
                      updateItem === "usernameandpwd") && (
                      <div>
                        <label
                          id={`${UUID}-username-label`}
                          htmlFor={`${UUID}-username`}
                          className="block mb-1"
                        >
                          Username
                        </label>
                        <input
                          id={`${UUID}-username`}
                          aria-labelledby={`${UUID}-username-label`}
                          aria-required="true"
                          type="text"
                          placeholder="Please enter a username"
                          {...register("username", {
                            required: "Please enter a user name",
                            validate: {
                              minimumCharCount: (v) =>
                                v.length >= 10 ||
                                "Username should be at least 10 characters long",
                              maximumCharCount: (v) =>
                                v.length <= 50 ||
                                "Username should not more than 50 characters",
                              illegalLetter: (v) =>
                                v.replace(/[A-Za-z0-9\.\-\_]/g, "").length <
                                  1 ||
                                "Username only allow Alphanumeric characters and Underscore (_), hyphen (-) and dot (.)",
                            },
                          })}
                          onKeyUp={() => {
                            setusernameError("");
                            setshowUsernameError(false);
                          }}
                          onKeyDown={() => {
                            setusernameError("");
                            setshowUsernameError(false);
                          }}
                        />
                        {errors.username?.message && (
                          <FormInputError
                            errorMessage={errors.username?.message}
                          />
                        )}
                        {showUsernameError && (
                          <FormInputError errorMessage={usernameError} />
                        )}
                        <div
                          id={`${UUID}-new-username-help`}
                          className="mt-1 mb-2"
                        >
                          <div className="text-xxs font-semibold">
                            In order to protect your account, follow these
                            username guidelines:
                          </div>
                          <ul className="list-disc ml-6">
                            <li className="text-xxs text-theme-text-tertiary">
                              Make sure the length of your username is at least
                              10 alphanumeric characters.
                            </li>
                            <li className="text-xxs text-theme-text-tertiary">
                              Make sure the length of your username is not more
                              than 50 alphanumeric characters.
                            </li>
                            <li className="text-xxs text-theme-text-tertiary">
                              Alphanumeric characters allowed.
                            </li>
                            <li className="text-xxs text-theme-text-tertiary">
                              Underscore, hyphen and dot allowed.
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* current password */}
                    <div className="relative mb-2">
                      <label
                        id={`${UUID}-current-password-label`}
                        htmlFor={`${UUID}-current-password`}
                        className="block mb-1"
                      >
                        Current password
                      </label>
                      <input
                        id={`${UUID}-current-password`}
                        aria-labelledby={`${UUID}-current-password-label`}
                        aria-required="true"
                        type={openEyeCurrentPassword ? "password" : "text"}
                        {...register("currentPassword", {
                          required: "Please enter your current password",
                        })}
                        onKeyUp={() => {
                          setErrorText("");
                          setShowErrorText(false);
                        }}
                        onKeyDown={() => {
                          setErrorText("");
                          setShowErrorText(false);
                        }}
                      />
                      <div className="absolute top-8 right-0.5">
                        {openEyeCurrentPassword ? (
                          <AiOutlineEye
                            onClick={toggleCurrentPasswordEye}
                          ></AiOutlineEye>
                        ) : (
                          <AiOutlineEyeInvisible
                            onClick={toggleCurrentPasswordEye}
                          ></AiOutlineEyeInvisible>
                        )}
                      </div>
                      {errors.currentPassword?.message && (
                        <FormInputError
                          errorMessage={errors.currentPassword?.message}
                        />
                      )}
                      {showErrorText && (
                        <FormInputError errorMessage={errorText} />
                      )}
                    </div>
                    {(updateItem === "password" ||
                      updateItem === "usernameandpwd") && (
                      <>
                        <div className="relative">
                          <label
                            id={`${UUID}-new-password-label`}
                            htmlFor={`${UUID}-new-password`}
                            className="block mb-1"
                          >
                            New password
                          </label>
                          <div className={"relative"}>
                            <input
                              id={`${UUID}-new-password`}
                              aria-labelledby={`${UUID}-new-password-label`}
                              aria-describedby={`${UUID}-new-password-help`}
                              aria-required="true"
                              type={openEyePassword ? "password" : "text"}
                              {...register("newPassword", {
                                required: "Please enter a new password",
                                validate: {
                                  minimumCharCount: (v) =>
                                    v.length >= 10 ||
                                    "Password should be at least 10 characters long and less than 61 characters",
                                  maximumCharCount: (v) =>
                                    v.length <= 60 ||
                                    "Password should be at least 10 characters long and less than 61 characters",
                                  hasLowerCaseLetter: (v) =>
                                    /^(?=.*[a-z])/.test(v) ||
                                    "Password should have at least one lowercase letter",
                                  hasCapitalizedCaseLetter: (v) =>
                                    /^(?=.*[A-Z])/.test(v) ||
                                    "Password should have at least one capitalized letter",
                                  hasNumericLetter: (v) =>
                                    /^(?=.*[0-9])/.test(v) ||
                                    "Password should have at least one numeric character",
                                  allowedSpecialChars: (v) =>
                                    /^(?=.*[!@#\$%&\*\(\)])/.test(v) ||
                                    "Password should have at least one special characters: ! @ # $ % & * ( )",
                                  illegalLetter: (v) =>
                                    v.replace(/[A-Za-z0-9!@#\$%&\*\(\)]/g, "")
                                      .length < 1 ||
                                    "MUST USE ONLY ! @ # $ % & * ( ) AS A SPECIAL CHARACTER",
                                },
                              })}
                              onKeyUp={() => {
                                setpasswordError("");
                                setshowPasswordError(false);
                              }}
                              onKeyDown={() => {
                                setpasswordError("");
                                setshowPasswordError(false);
                              }}
                            />
                          </div>
                          <div className="absolute top-8 right-0.5">
                            {openEyePassword ? (
                              <AiOutlineEye
                                onClick={togglePasswordEye}
                              ></AiOutlineEye>
                            ) : (
                              <AiOutlineEyeInvisible
                                onClick={togglePasswordEye}
                              ></AiOutlineEyeInvisible>
                            )}
                          </div>
                          {errors.newPassword?.message && (
                            <FormInputError
                              errorMessage={errors.newPassword?.message}
                            />
                          )}
                          {showPasswordError && (
                            <FormInputError errorMessage={passwordError} />
                          )}
                          <div
                            id={`${UUID}-new-password-help`}
                            className="mt-1 mb-2"
                          >
                            <div className="text-xxs font-semibold">
                              In order to protect your account, follow these
                              password guidelines:
                            </div>
                            <ul className="list-disc ml-6">
                              <li className="text-xxs text-theme-text-tertiary">
                                Make sure the length of your password is at
                                least 10 alphanumeric characters.
                              </li>
                              <li className="text-xxs text-theme-text-tertiary">
                                At least one special character:{" "}
                                {"! @ # $ % & * ( )"}
                              </li>
                              <li className="text-xxs text-theme-text-tertiary">
                                At least one upper-case and lower-case
                                character.
                              </li>
                              <li className="text-xxs text-theme-text-tertiary">
                                At least one numerical digit.
                              </li>
                              <li className="text-xxs text-theme-text-tertiary">
                                New Password cannot be one of the previous 15
                                passwords.
                              </li>
                            </ul>
                          </div>
                        </div>
                        {/* confirm password */}
                        <div className="relative">
                          <label
                            id={`${UUID}-confirm-password-label`}
                            htmlFor={`${UUID}-confirm-password`}
                            className="block mb-1"
                          >
                            Confirm password
                          </label>
                          <div className={"relative"}>
                            <input
                              id={`${UUID}-confirm-password`}
                              aria-labelledby={`${UUID}-confirm-password-label`}
                              aria-describedby={`${UUID}-new-password-help`}
                              aria-required="true"
                              type={
                                openEyeConfirmPassword ? "password" : "text"
                              }
                              {...register("confirmedPassword", {
                                required:
                                  "Please enter your new password again",
                                validate: (value) => {
                                  if (getValues("newPassword") !== value)
                                    return "Passwords do not match";
                                },
                              })}
                            />
                          </div>
                          <div className="absolute top-8 right-0.5">
                            {openEyeConfirmPassword ? (
                              <AiOutlineEye
                                onClick={toggleConfirmPasswordEye}
                              ></AiOutlineEye>
                            ) : (
                              <AiOutlineEyeInvisible
                                onClick={toggleConfirmPasswordEye}
                              ></AiOutlineEyeInvisible>
                            )}
                          </div>
                          {errors.confirmedPassword?.message && (
                            <FormInputError
                              errorMessage={errors.confirmedPassword?.message}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    Cancel
                    <span className="sr-only">
                      changes to your security settings
                    </span>
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    Next
                  </button>
                </div>
              </form>
            )}
            {step === 2 && (
              <form onSubmit={handleSubmit(submitStepTwo)}>
                <div className="w-full mx-auto sm:w-4/5">
                  <div className="text-base font-semibold">
                    Confirm Your Identity
                  </div>
                  <div className="mx-auto mt-4">
                    <div>Thanks for keeping your account secure.</div>
                    <div>
                      {" "}
                      Please check your email for a confirmation code. This code
                      is valid for{" "}
                      {parseInt(parseInt(config.OTP_EXPIRY_TIME) / 60000)}{" "}
                      minutes.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mt-4">
                    {/* Your Verification Code */}
                    <div>
                      <label
                        id={`${UUID}-verification-label`}
                        htmlFor={`${UUID}-verification`}
                        className="block mb-1"
                      >
                        Your Corfirmation Code
                      </label>
                      <input
                        id={`${UUID}-verification`}
                        aria-labelledby={`${UUID}-verification-label`}
                        aria-required="true"
                        type="text"
                        placeholder="Please enter 6-digit code"
                        {...register("verificationCode", {
                          required:
                            "Please enter Confirmation code before continuing.",
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid code (6 digits)",
                          },
                        })}
                        onKeyUp={() => {
                          setotpError("");
                          setshowOtpError(false);
                        }}
                        onKeyDown={() => {
                          setotpError("");
                          setshowOtpError(false);
                        }}
                      />
                      {errors.verificationCode?.message && (
                        <FormInputError
                          errorMessage={errors.verificationCode.message}
                        />
                      )}
                      {showOtpError && (
                        <FormInputError errorMessage={otpError} />
                      )}
                    </div>
                    <Countdown
                      date={currentCounterTime}
                      key={currentCounterIndex}
                      renderer={renderer}
                    />
                  </div>
                </div>
                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    Cancel
                    <span className="sr-only">
                      changes to your security settings
                    </span>
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    Confirm
                    <span className="sr-only">
                      changes to your security settings
                    </span>
                  </button>
                </div>
              </form>
            )}{" "}
            {step === 3 && (
              <>
                <div className="p-4 mx-auto md:p-6">
                  <h2 className="font-body font-bold text-xl">
                    Your security settings have been updated successfully.
                  </h2>
                  <p className="text-sm">
                    For the security of your information, please do not share
                    your username and password with anyone.
                  </p>
                  <div className="text-xl text-center font-semibold mt-4 md:mt-6">
                    Thank you.
                  </div>
                  <div className="text-center mt-4 md:mt-6">
                    <button
                      type="button"
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      onClick={() => window.location.reload(false)}
                    >
                      {" "}
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
